/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { v7 as uuidv7 } from "uuid";

import { useScheduleStore } from "@/stores/useScheduleStore";

import { TUTORIAL_IDS, tableNameParams } from "../../../../shared";
import { useDialog } from "../../../PageHeader/actions/hooks/useDialog";
import { useStyles } from "../../styles";
import { ResourceParams, ScheduleDataDto } from "../../types";
import CreateScheduleDialog from "../CreateScheduleDialog/CreateScheduleDialog";

import SingleScheduleCard from "./SingleScheduleCard/SingleScheduleCard";

interface ScheduleCardsProps {
  schedulesData: ScheduleDataDto[] | null;
  isScheduleDataLoading: boolean;
  globalScheduleExists: boolean;
  limitReached: boolean;
  resourceParams: ResourceParams;
  canManageSchedules: boolean;
  getSchedulesData: () => Promise<void>;
}

const ScheduleCards = ({
  schedulesData,
  isScheduleDataLoading,
  globalScheduleExists,
  limitReached,
  resourceParams,
  canManageSchedules,
  getSchedulesData
}: ScheduleCardsProps) => {
  const { isDialogOpen, openDialog, closeDialog } = useDialog();

  const { selectedSchedule, setSelectedSchedule } = useScheduleStore();

  const { scheduleCards } = useStyles();

  const selectedScheduleId = selectedSchedule?.id;

  const { resourceName, actionCreate } = resourceParams;

  const handleCardClick = (
    isCreateNewSchedule: boolean | undefined,
    scheduleData: ScheduleDataDto | null
  ) => {
    const isAlreadySelected = selectedScheduleId === scheduleData?.id;

    setSelectedSchedule(
      isCreateNewSchedule || isAlreadySelected ? null : scheduleData
    );

    if (isCreateNewSchedule) {
      openDialog?.(actionCreate);
    }
  };

  return (
    <>
      <div css={css(scheduleCards)} id={TUTORIAL_IDS.MainScheduleCards}>
        {isScheduleDataLoading ? (
          new Array(3).fill(null).map((_, index) => {
            return (
              <SingleScheduleCard
                key={uuidv7()}
                scheduleData={null}
                resourceName={resourceName}
                index={index}
                isScheduleDataLoading
                globalScheduleExists={globalScheduleExists}
                getSchedulesData={getSchedulesData}
              />
            );
          })
        ) : (
          <>
            {!globalScheduleExists && (
              <SingleScheduleCard
                scheduleData={null}
                globalScheduleExists={false}
                isScheduleDataLoading={isScheduleDataLoading}
                resourceName={resourceName}
                getSchedulesData={getSchedulesData}
              />
            )}

            {schedulesData?.map(scheduleData => (
              <SingleScheduleCard
                key={scheduleData.id}
                scheduleData={scheduleData}
                globalScheduleExists={true}
                resourceName={resourceName}
                isSelected={selectedScheduleId === scheduleData.id}
                getSchedulesData={getSchedulesData}
                handleCardClick={handleCardClick}
              />
            ))}

            {!limitReached && canManageSchedules && (
              <SingleScheduleCard
                scheduleData={null}
                globalScheduleExists={true}
                resourceName={resourceName}
                isCreateNewSchedule
                isSelected={!selectedScheduleId}
                getSchedulesData={getSchedulesData}
                handleCardClick={handleCardClick}
              />
            )}
          </>
        )}
      </div>
      <CreateScheduleDialog
        resourceParams={tableNameParams[resourceName]}
        isOpen={isDialogOpen[actionCreate]}
        onClose={closeDialog}
        getSchedulesData={getSchedulesData}
      />
    </>
  );
};

export default ScheduleCards;
